import { usePaymentTypesWarm } from '../modals/usePaymentTypes';
import { useEffect, useMemo, useRef, useState } from 'react';
import { POS_PERMISSIONS } from '@matix/types';
import { useReceiptsStore } from '../../store/receipts';
import { useUI } from '../../store/ui';
import { toast } from '../../store/toast';
import { useLang } from '../../lib/language';
import { useCanI } from '../../lib/permissions';
import { RefreshIcon } from '../../components/icons';
import {
  formatReceiptDateTime,
  formatReceiptFullDateTime,
  formatNetValue,
  formatCustomerCell,
  computeTotalPages,
  splitTableLabel,
  joinDateTimeLocal,
  splitDateTimeLocal,
} from './helpers';
import { exportReceiptsExcel, receiptsRangeLabel } from './receiptsExcel';
import { ExcelButton } from '../reports/reportShared';
import { VoidReceiptDialog } from './VoidReceiptDialog';
import type { ReceiptListItem, ReceiptListScope, ReceiptListType, ReceiptOrderTypeFilter } from '@matix/types';
import { RECEIPT_ORDER_TYPES, receiptOrderTypeLabel, receiptOrderTypeName } from '@matix/types';

/**
 * Receipts screen — Phase 5d.3.1.
 *
 * Layout matches the legacy MatixERP Orders History page:
 *
 *   ┌─ Order Details ──┬─ Filters bar ──────────────────────┐
 *   │ Order #470       │ Search · Scope · (more later)      │
 *   │ Creation: ...    ├─ Table ────────────────────────────┤
 *   │ Payment: Multi   │ # | Type | Customer | Total | Date │
 *   │ Items:           │ ───────────────────────────────────│
 *   │   • Item A x1    │ 470  Take away  Sara   323.95  ... │
 *   │ Subtotal/Total   │ 469  Walk-in    —      120.23  ... │
 *   │ ───────────────  │                                    │
 *   │ [Open] [Pay]     ├─ Pagination ───────────────────────┤
 *   │ [Print] [Return] │ First | Prev | 1 2 3 | Next | Last │
 *   │ [Edit]           │                                    │
 *   └──────────────────┴────────────────────────────────────┘
 *
 * Design priorities:
 *   - Familiar to staff who know the legacy UI
 *   - Server-computed `can_edit` gates the Edit button
 *   - Open-it / Pay / Print / Return are placeholders today —
 *     wired to actual flows in subsequent phases (Phase 5d.4 owns
 *     Edit; future phases own the rest)
 *
 * State lives in useReceiptsStore (paging + selection + filters).
 */
export interface ReceiptsScreenProps {
  /** Stub callbacks — wired up in later phases. */
  onView?: (receiptId: number) => void;
  onReprint?: (receiptId: number) => void;
  onEdit?: (receiptId: number) => void;
  onExchange?: (receiptId: number) => void;
}

export function ReceiptsScreen(props: ReceiptsScreenProps) {
  const { t, lang } = useLang();
  const rows = useReceiptsStore((s) => s.rows);
  const total = useReceiptsStore((s) => s.total);
  const loading = useReceiptsStore((s) => s.loading);
  const error = useReceiptsStore((s) => s.error);
  const scope = useReceiptsStore((s) => s.scope);
  const type = useReceiptsStore((s) => s.type);
  const orderType = useReceiptsStore((s) => s.orderType);
  const paymentTypeId = useReceiptsStore((s) => s.paymentTypeId);
  const search = useReceiptsStore((s) => s.search);
  const dateFrom = useReceiptsStore((s) => s.dateFrom);
  const dateTo = useReceiptsStore((s) => s.dateTo);
  const timeFrom = useReceiptsStore((s) => s.timeFrom);
  const timeTo = useReceiptsStore((s) => s.timeTo);
  const hasOpenShift = useReceiptsStore((s) => s.hasOpenShift);
  const initialized = useReceiptsStore((s) => s.initialized);
  const page = useReceiptsStore((s) => s.page);
  const limit = useReceiptsStore((s) => s.limit);
  const selectedId = useReceiptsStore((s) => s.selectedId);

  const setScope = useReceiptsStore((s) => s.setScope);
  const setType = useReceiptsStore((s) => s.setType);
  const setOrderType = useReceiptsStore((s) => s.setOrderType);
  const setPaymentTypeId = useReceiptsStore((s) => s.setPaymentTypeId);
  // The till's own payment-method list (already warm from login).
  const paymentTypes = usePaymentTypesWarm().data?.payment_types ?? [];
  const selectedPaymentType = paymentTypes.find((p) => p.id === paymentTypeId) ?? null;
  const setSearch = useReceiptsStore((s) => s.setSearch);
  const setDateTimeRange = useReceiptsStore((s) => s.setDateTimeRange);
  const clearDateRange = useReceiptsStore((s) => s.clearDateRange);
  const refreshNow = useReceiptsStore((s) => s.refreshNow);
  const fetchAllForExport = useReceiptsStore((s) => s.fetchAllForExport);
  const setPage = useReceiptsStore((s) => s.setPage);
  const selectRow = useReceiptsStore((s) => s.selectRow);
  const refresh = useReceiptsStore((s) => s.refresh);
  const voidReceipt = useReceiptsStore((s) => s.void);

  // The Void button is gated on the legacy `Cancel Order` permission — the
  // ERP's own permission for this act, held under POS → Orders on every
  // tenant, so nothing has to be granted before the feature works. The server
  // checks the same name again; this only decides whether to render it.
  const canVoid = useCanI(POS_PERMISSIONS.CancelOrder);
  const [voidTarget, setVoidTarget] = useState<ReceiptListItem | null>(null);
  const [voiding, setVoiding] = useState(false);
  const cancelReasons = useReceiptsStore((s) => s.cancelReasons);
  const loadCancelReasons = useReceiptsStore((s) => s.loadCancelReasons);

  // The void dialog's reason list, fetched once and only for someone who can
  // actually void. Ahead of the dialog rather than inside it: the cashier
  // opens that dialog with a receipt they mean to cancel, and a dropdown that
  // populates a beat later is a dropdown that gets voided past.
  useEffect(() => {
    if (canVoid) void loadCancelReasons();
  }, [canVoid, loadCancelReasons]);

  async function confirmVoid(
    cancelReasonId: number | null,
    note: string | null,
  ): Promise<void> {
    const target = voidTarget;
    if (!target) return;
    setVoiding(true);
    const result = await voidReceipt(target.id, cancelReasonId, note);
    setVoiding(false);
    // The dialog closes on success only. A refusal — a posted voucher, a
    // closed shift — is information the cashier needs while still looking at
    // the receipt it is about.
    if (result.ok) setVoidTarget(null);
    toast(result.message, result.ok ? 'success' : 'error');
  }

  // Refresh on mount.
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search refresh.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => void refresh(), 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Auto-select the first row when results change so the details panel
  // is never blank — matches legacy behavior (the legacy ERP highlights
  // the first row by default).
  useEffect(() => {
    if (!selectedId && rows.length > 0) {
      selectRow(rows[0]!.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const selectedRow = useMemo(
    () => rows.find((r) => r.id === selectedId) ?? null,
    [rows, selectedId],
  );

  const totalPages = computeTotalPages(total, limit);

  // How each end of the active window reads in the header line. The workbook's
  // subtitle and filename are built the same way, by `receiptsRangeLabel`.
  const fromLabel = dateFrom ? `${dateFrom}${timeFrom ? ` ${timeFrom}` : ''}` : '';
  const toLabel = dateTo ? `${dateTo}${timeTo ? ` ${timeTo}` : ''}` : '';

  const [exporting, setExporting] = useState(false);

  /**
   * Export every receipt the current filters match — not the page on screen.
   *
   * The rows are re-fetched rather than taken from the store: the table holds
   * twenty, and a cashier exporting "this week" means the week. The button is
   * only rendered once the list has something in it, so the file is never an
   * empty sheet.
   */
  async function exportExcel(): Promise<void> {
    if (exporting) return;
    setExporting(true);
    try {
      const { rows: allRows, truncated } = await fetchAllForExport();
      if (allRows.length === 0) {
        toast(t({ en: 'Nothing to export.', ar: 'لا يوجد ما يمكن تصديره.' }), 'error');
        return;
      }
      exportReceiptsExcel({
        rows: allRows,
        truncated,
        filters: {
          scope, type, search, dateFrom, dateTo, timeFrom, timeTo,
          orderTypeLabel: receiptOrderTypeLabel(orderType, lang === 'ar' ? 'ar' : 'en'),
          paymentTypeLabel: selectedPaymentType
            ? (lang === 'ar' ? selectedPaymentType.name_ar || selectedPaymentType.name : selectedPaymentType.name)
            : '',
        },
        rangeLabel: receiptsRangeLabel(
          { scope, type, search, dateFrom, dateTo, timeFrom, timeTo },
          t({ en: 'all-time', ar: 'كل الأوقات' }),
        ),
        rightToLeft: lang === 'ar',
        labels: {
          // "الفواتير", not the screen's own "الإيصالات": the workbook is the
          // document people file and forward, and invoices is the word the
          // tenants use for it. On the user's instruction, 2026-09-16. It
          // titles the sheet, names the sheet tab AND names the file.
          title: t({ en: 'Receipts', ar: 'الفواتير' }),
          orderNo: t({ en: 'Order #', ar: 'طلب رقم' }),
          reference: t({ en: 'Reference', ar: 'المرجع' }),
          type: t({ en: 'Type', ar: 'النوع' }),
          orderType: t({ en: 'Order type', ar: 'نوع الطلب' }),
          customer: t({ en: 'Customer', ar: 'العميل' }),
          phone: t({ en: 'Phone', ar: 'الهاتف' }),
          payment: t({ en: 'Payment', ar: 'الدفع' }),
          total: t({ en: 'Total', ar: 'الإجمالي' }),
          date: t({ en: 'Date', ar: 'التاريخ' }),
          time: t({ en: 'Time', ar: 'الوقت' }),
          status: t({ en: 'Status', ar: 'الحالة' }),
          sale: t({ en: 'Sale', ar: 'بيع' }),
          return: t({ en: 'Return', ar: 'مرتجع' }),
          credit: t({ en: 'Credit', ar: 'آجل' }),
          voided: t({ en: 'Voided', ar: 'ملغى' }),
          posted: t({ en: 'Posted', ar: 'مُرحّل' }),
          open: t({ en: 'Open', ar: 'مفتوح' }),
          closedShift: t({ en: 'Closed shift', ar: 'وردية مغلقة' }),
          scopeCurrentShift: t({ en: 'Current shift', ar: 'الوردية الحالية' }),
          scopeAll: t({ en: 'All receipts', ar: 'كل الفواتير' }),
          typeSales: t({ en: 'Sales', ar: 'مبيعات' }),
          typeReturns: t({ en: 'Returns', ar: 'مرتجعات' }),
          totalRow: t({ en: 'Net total', ar: 'الصافي' }),
          truncatedNote: t({
            en: 'Partial export — only the most recent receipts of this window are included. Narrow the filter to export the rest.',
            ar: 'تصدير جزئي — يشمل أحدث الفواتير في هذه الفترة فقط. ضيّق الفلتر لتصدير الباقي.',
          }),
        },
      });
      if (truncated) {
        toast(
          t({
            en: 'Exported the most recent receipts only — narrow the date range for the rest.',
            ar: 'تم تصدير أحدث الفواتير فقط — ضيّق نطاق التاريخ لتصدير الباقي.',
          }),
          'error',
        );
      }
    } catch (err) {
      toast(
        err instanceof Error && err.message
          ? err.message
          : t({ en: "Couldn't export the receipts.", ar: 'تعذّر تصدير الفواتير.' }),
        'error',
      );
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="receipts-page">
      {/* ── LEFT: Order Details panel ─────────────────────────────── */}
      <aside className="receipts-details">
        <h2 className="receipts-details-title">{t({ en: 'Order details', ar: 'تفاصيل الطلب' })}</h2>
        {selectedRow ? (
          <SelectedReceiptDetails
            row={selectedRow}
            onView={props.onView}
            onReprint={props.onReprint}
            onEdit={props.onEdit}
            onExchange={props.onExchange}
            canVoid={canVoid}
            onVoid={() => setVoidTarget(selectedRow)}
          />
        ) : (
          <div className="receipts-details-empty">
            <p>{t({ en: 'Select a receipt to see its details.', ar: 'اختر إيصالاً لعرض تفاصيله.' })}</p>
          </div>
        )}
      </aside>

      {/* ── RIGHT: Filters + Table + Pagination ───────────────────── */}
      <section className="receipts-main">
        <header className="receipts-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              type="button"
              className="receipts-btn receipts-btn-secondary"
              onClick={() => useUI.getState().setTab('walkin')}
              title={t({ en: 'Back to the sale screen', ar: 'العودة إلى شاشة البيع' })}
            >
              ← {t({ en: 'Back to sale', ar: 'العودة إلى البيع' })}
            </button>
            <h2>{t({ en: 'Receipts', ar: 'الإيصالات' })}</h2>
          </div>
          <div className="receipts-meta">
            {total} {t({ en: 'total', ar: 'الإجمالي' })}
            {scope === 'current_shift' && hasOpenShift ? t({ en: ' · current shift', ar: ' · الوردية الحالية' }) : ''}
            {/* "All-time" is only honest with no date window on it — and
                there is one by default, so say which. */}
            {scope === 'all' && !dateFrom && !dateTo ? t({ en: ' · all-time', ar: ' · كل الأوقات' }) : ''}
            {/* One whole day still reads as one date; anything narrower says
                the clock, because "today" and "today 14:00 → 16:00" are two
                very different totals to be looking at. */}
            {dateFrom && dateTo && dateFrom === dateTo && !timeFrom && !timeTo ? ` · ${dateFrom}` : ''}
            {dateFrom && dateTo && (dateFrom !== dateTo || timeFrom || timeTo)
              ? ` · ${fromLabel} → ${toLabel}`
              : ''}
            {dateFrom && !dateTo ? t({ en: ` · since ${fromLabel}`, ar: ` · منذ ${fromLabel}` }) : ''}
            {!dateFrom && dateTo ? t({ en: ` · until ${toLabel}`, ar: ` · حتى ${toLabel}` }) : ''}
            {orderType !== 'all' ? ` · ${receiptOrderTypeLabel(orderType, lang === 'ar' ? 'ar' : 'en')}` : ''}
            {loading ? t({ en: ' · refreshing…', ar: ' · جارٍ التحديث…' }) : ''}
          </div>
        </header>

        <div className="receipts-filters">
          <input
            type="search"
            className="receipts-input receipts-search"
            placeholder={t({ en: 'Search by order #, website order #, customer name, or phone', ar: 'ابحث برقم الطلب أو رقم طلب الموقع أو اسم العميل أو الهاتف' })}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={t({ en: 'Search receipts', ar: 'بحث في الإيصالات' })}
          />
          {/* Date + time range. Defaults to the last 60 days, whole days, so
              a recent customer's receipt is one search away; widen it to
              find an older receipt, or narrow it to an hour of it — a branch
              that ran past midnight, or a supervisor asking what was rung
              since two o'clock, needs the clock and not only the calendar.
              The time lives INSIDE each picker so the bar still reads
              "From … To …". The `from` input caps `to` and vice versa, so a
              backwards window — which can only ever return nothing — is not
              reachable from the UI at all. */}
          <div className="receipts-dates">
            <label className="receipts-date">
              <span>{t({ en: 'From', ar: 'من' })}</span>
              <input
                type="datetime-local"
                step={60}
                className="receipts-input receipts-date-input"
                value={joinDateTimeLocal(dateFrom, timeFrom)}
                max={dateTo ? joinDateTimeLocal(dateTo, timeTo || '23:59') : undefined}
                onChange={(e) => {
                  const picked = splitDateTimeLocal(e.target.value);
                  setDateTimeRange(picked.date, dateTo, picked.time, timeTo);
                }}
                aria-label={t({ en: 'Receipts from date and time', ar: 'الإيصالات من تاريخ ووقت' })}
              />
            </label>
            <label className="receipts-date">
              <span>{t({ en: 'To', ar: 'إلى' })}</span>
              <input
                type="datetime-local"
                step={60}
                className="receipts-input receipts-date-input"
                // With no time picked the window runs to the END of the day,
                // so that is what the picker shows — 00:00 there would say the
                // opposite of what the query does.
                value={dateTo ? joinDateTimeLocal(dateTo, timeTo || '23:59') : ''}
                min={dateFrom ? joinDateTimeLocal(dateFrom, timeFrom) : undefined}
                onChange={(e) => {
                  const picked = splitDateTimeLocal(e.target.value);
                  setDateTimeRange(dateFrom, picked.date, timeFrom, picked.time);
                }}
                aria-label={t({ en: 'Receipts to date and time', ar: 'الإيصالات إلى تاريخ ووقت' })}
              />
            </label>
            {/* A small icon beside the pickers rather than a worded button:
                the worded one was squeezed out of the bar on a till-sized
                screen, and this is the control that turns the list into
                every receipt — the search box then filters all of them. */}
            {(dateFrom || dateTo) && (
              <button
                type="button"
                className="receipts-date-clear"
                onClick={() => clearDateRange()}
                title={t({ en: 'Reset dates — show every receipt', ar: 'إعادة ضبط التاريخ — عرض كل الإيصالات' })}
                aria-label={t({ en: 'Reset dates — show every receipt', ar: 'إعادة ضبط التاريخ — عرض كل الإيصالات' })}
              >
                ✕
              </button>
            )}
          </div>
          <div className="receipts-scope" role="tablist" aria-label={t({ en: 'Scope', ar: 'النطاق' })}>
            <button
              type="button"
              role="tab"
              aria-selected={scope === 'current_shift'}
              className={`receipts-scope-btn ${scope === 'current_shift' ? 'active' : ''}`}
              onClick={() => setScope('current_shift')}
            >
              {t({ en: 'Current shift', ar: 'الوردية الحالية' })}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={scope === 'all'}
              className={`receipts-scope-btn ${scope === 'all' ? 'active' : ''}`}
              onClick={() => setScope('all')}
            >
              {t({ en: 'All', ar: 'الكل' })}
            </button>
          </div>
          {/* All / Sales / Returns — the admin Orders screen's toggle.
              Filtered on the server (sales = 51 + 52, returns = 53), so the
              count, the paging and the Excel export all follow it. */}
          <div className="receipts-scope" role="tablist" aria-label={t({ en: 'Type', ar: 'النوع' })}>
            {(['all', 'sales', 'returns'] as ReceiptListType[]).map((tp) => (
              <button
                key={tp}
                type="button"
                role="tab"
                aria-selected={type === tp}
                className={`receipts-scope-btn ${type === tp ? 'active' : ''}`}
                onClick={() => setType(tp)}
              >
                {tp === 'all'
                  ? t({ en: 'All', ar: 'الكل' })
                  : tp === 'sales'
                    ? t({ en: 'Sales', ar: 'مبيعات' })
                    : t({ en: 'Returns', ar: 'مرتجعات' })}
              </button>
            ))}
          </div>
          {/* Order type — every type the stack writes (Inv_Receipt_h.OrderType
              1-5) plus "Not set" for an ERP receipt with none. A select, not
              one more toggle: six choices do not fit the bar as buttons. It
              turns blue while it filters, so a narrowed list never passes for
              the whole archive. Server-side, like the toggles beside it. */}
          <label className={`receipts-ordertype ${orderType !== 'all' ? 'active' : ''}`}>
            <span>{t({ en: 'Order type', ar: 'نوع الطلب' })}</span>
            <select
              className="receipts-input receipts-ordertype-select"
              value={orderType}
              onChange={(e) => setOrderType(e.target.value as ReceiptOrderTypeFilter)}
              aria-label={t({ en: 'Order type', ar: 'نوع الطلب' })}
            >
              <option value="all">{t({ en: 'All types', ar: 'كل الأنواع' })}</option>
              {RECEIPT_ORDER_TYPES.map((o) => (
                <option key={o.value} value={o.value}>
                  {lang === 'ar' ? o.ar : o.en}
                </option>
              ))}
            </select>
          </label>
          {/* Payment type — a receipt paid on it, alone or as one slice of a
              split. Server-side like the order type, same blue "active" look. */}
          <label className={`receipts-ordertype ${paymentTypeId != null ? 'active' : ''}`}>
            <span>{t({ en: 'Payment', ar: 'الدفع' })}</span>
            <select
              className="receipts-input receipts-ordertype-select"
              value={paymentTypeId ?? ''}
              onChange={(e) => setPaymentTypeId(e.target.value ? Number(e.target.value) : null)}
              aria-label={t({ en: 'Payment type', ar: 'نوع الدفع' })}
            >
              <option value="">{t({ en: 'All payments', ar: 'كل طرق الدفع' })}</option>
              {paymentTypes.map((p) => (
                <option key={p.id} value={p.id}>
                  {lang === 'ar' ? p.name_ar || p.name : p.name}
                </option>
              ))}
            </select>
          </label>
          {/* Refresh — fetch receipts saved since the list loaded (web /
              online orders arriving on khwada's till). Same filters, same page. */}
          <button
            type="button"
            className="receipts-btn receipts-btn-refresh"
            onClick={() => void refreshNow()}
            disabled={loading}
            title={t({ en: 'Get the newest receipts', ar: 'جلب أحدث الإيصالات' })}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <RefreshIcon size={15} />
            {t({ en: 'Refresh', ar: 'تحديث' })}
          </button>
          {/* RENDERED only once the list has rows, the way the reports' own
              export button is: a spreadsheet of an empty archive is a file
              nobody wants, and the button appearing is the clearest sign the
              filter matched something. */}
          {rows.length > 0 && (
            <div className="receipts-export">
              {exporting ? (
                <span className="receipts-export-busy">
                  {t({ en: 'Preparing…', ar: 'جارٍ التجهيز…' })}
                </span>
              ) : (
                <ExcelButton onClick={() => void exportExcel()} />
              )}
            </div>
          )}
        </div>

        {/* Body: state-driven (loading / error / empty / table) */}
        <div className="receipts-body">
          {!initialized && loading && (
            <div className="receipts-state">{t({ en: 'Loading receipts…', ar: 'جارٍ تحميل الإيصالات…' })}</div>
          )}

          {error && (
            <div className="receipts-state receipts-state-error">
              <strong>{t({ en: "Couldn't load receipts.", ar: 'تعذّر تحميل الإيصالات.' })}</strong>
              <p>{error}</p>
              <button
                type="button"
                className="receipts-btn receipts-btn-primary"
                onClick={() => void refresh()}
              >
                {t({ en: 'Retry', ar: 'إعادة المحاولة' })}
              </button>
            </div>
          )}

          {initialized && !error && rows.length === 0 && (
            <ReceiptsEmptyState
              scope={scope}
              search={search}
              hasOpenShift={hasOpenShift}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onClearDates={clearDateRange}
            />
          )}

          {rows.length > 0 && (
            <table className="receipts-table" role="grid">
              <thead>
                <tr>
                  <th>{t({ en: 'Order #', ar: 'طلب رقم' })}</th>
                  <th>{t({ en: 'Reference', ar: 'المرجع' })}</th>
                  <th>{t({ en: 'Type', ar: 'النوع' })}</th>
                  <th>{t({ en: 'Order type', ar: 'نوع الطلب' })}</th>
                  <th>{t({ en: 'Customer', ar: 'العميل' })}</th>
                  <th>{t({ en: 'Payment', ar: 'الدفع' })}</th>
                  <th className="num">{t({ en: 'Total', ar: 'الإجمالي' })}</th>
                  <th>{t({ en: 'Date', ar: 'التاريخ' })}</th>
                  <th>{t({ en: 'Status', ar: 'الحالة' })}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    // `voided` greys the row and strikes its total through, so
                    // a cancelled sale is recognisable without reading the
                    // Status cell at the far end of a wide table.
                    className={`${selectedId === row.id ? 'selected' : ''}${row.is_canceled ? ' voided' : ''}`}
                    onClick={() => selectRow(row.id)}
                  >
                    <td className="entry-no" data-label={t({ en: 'Order #', ar: 'طلب رقم' })}>#{row.entry_no}</td>
                    <td data-label={t({ en: 'Reference', ar: 'المرجع' })}>{row.reference_no ?? '—'}</td>
                    <td data-label={t({ en: 'Type', ar: 'النوع' })}>
                      {row.is_return ? (
                        <span className="receipts-badge return">{t({ en: 'Return', ar: 'مرتجع' })}</span>
                      ) : row.is_credit ? (
                        <span
                          className="receipts-badge credit"
                          title={t({ en: "Credit-sales invoice — on the customer's account", ar: 'فاتورة مبيعات آجلة — على حساب العميل' })}
                        >
                          {t({ en: 'Credit', ar: 'آجل' })}
                        </span>
                      ) : (
                        <span className="receipts-badge sale">{t({ en: 'Sale', ar: 'بيع' })}</span>
                      )}
                    </td>
                    <td className="order-type-cell" data-label={t({ en: 'Order type', ar: 'نوع الطلب' })}>
                      {receiptOrderTypeName(row.order_type, lang === 'ar' ? 'ar' : 'en') || '—'}
                    </td>
                    <td data-label={t({ en: 'Customer', ar: 'العميل' })}>{formatCustomerCell(row.customer_name, row.customer_phone)}</td>
                    <td data-label={t({ en: 'Payment', ar: 'الدفع' })}>{row.payment_label}</td>
                    <td className="num" data-label={t({ en: 'Total', ar: 'الإجمالي' })}>{formatNetValue(row.net_value)}</td>
                    <td className="date-cell" data-label={t({ en: 'Date', ar: 'التاريخ' })}>{formatReceiptDateTime(row.entry_date)}</td>
                    <td data-label={t({ en: 'Status', ar: 'الحالة' })}>
                      {/* Voided first and alone: once a receipt is cancelled,
                          whether its voucher was posted or its shift is still
                          open stops being the thing to say about it. */}
                      {row.is_canceled ? (
                        <span className="receipts-badge voided">{t({ en: 'Voided', ar: 'ملغى' })}</span>
                      ) : row.is_voucher_posted ? (
                        <span className="receipts-badge posted">{t({ en: 'Posted', ar: 'مُرحّل' })}</span>
                      ) : (row.in_open_shift ?? row.in_current_shift) ? (
                        <span className="receipts-badge open">{t({ en: 'Open', ar: 'مفتوح' })}</span>
                      ) : (
                        <span className="receipts-badge closed">{t({ en: 'Closed shift', ar: 'وردية مغلقة' })}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination — only shown when there's something to paginate. */}
        {rows.length > 0 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            onChange={setPage}
          />
        )}
      </section>

      <VoidReceiptDialog
        row={voidTarget}
        busy={voiding}
        reasons={cancelReasons}
        onCancel={() => setVoidTarget(null)}
        onConfirm={(reasonId, note) => void confirmVoid(reasonId, note)}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Subcomponents
// ────────────────────────────────────────────────────────────────────────

interface SelectedDetailsProps {
  row: ReceiptListItem;
  onView: ReceiptsScreenProps['onView'];
  onReprint: ReceiptsScreenProps['onReprint'];
  onEdit: ReceiptsScreenProps['onEdit'];
  onExchange: ReceiptsScreenProps['onExchange'];
  /** Does this user hold `Cancel Order` at all — decides whether Void exists. */
  canVoid: boolean;
  onVoid: () => void;
}

function SelectedReceiptDetails({
  row,
  onView,
  onReprint,
  onEdit,
  onExchange,
  canVoid,
  onVoid,
}: SelectedDetailsProps) {
  const { t } = useLang();
  // The list endpoint is intentionally light. Item lines + payment
  // breakdown live in a separate detail endpoint (Phase 5d.4 will
  // wire that). For now we show the header fields the list already
  // gives us — enough to identify a receipt before opening it.
  return (
    <div className="receipts-details-content">
      <div className="receipts-details-row">
        <span className="receipts-details-label">{t({ en: 'Order #', ar: 'طلب رقم' })}</span>
        <span className="receipts-details-value strong">#{row.entry_no}</span>
      </div>
      {row.reference_no && (
        <div className="receipts-details-row">
          <span className="receipts-details-label">{t({ en: 'Reference', ar: 'المرجع' })}</span>
          <span className="receipts-details-value">{row.reference_no}</span>
        </div>
      )}
      <div className="receipts-details-row">
        <span className="receipts-details-label">{t({ en: 'Created', ar: 'تاريخ الإنشاء' })}</span>
        <span className="receipts-details-value">{formatReceiptFullDateTime(row.entry_date)}</span>
      </div>
      <div className="receipts-details-row">
        <span className="receipts-details-label">{t({ en: 'Customer', ar: 'العميل' })}</span>
        <span className="receipts-details-value">
          {formatCustomerCell(row.customer_name, row.customer_phone)}
        </span>
      </div>
      {row.table_label && (
        <div className="receipts-details-row">
          <span className="receipts-details-label">{splitTableLabel(row.table_label).term}</span>
          <span className="receipts-details-value">{splitTableLabel(row.table_label).value}</span>
        </div>
      )}
      <div className="receipts-details-row">
        <span className="receipts-details-label">{t({ en: 'Payment', ar: 'الدفع' })}</span>
        <span className="receipts-details-value">{row.payment_label}</span>
      </div>
      <div className="receipts-details-row">
        <span className="receipts-details-label">{t({ en: 'Total', ar: 'الإجمالي' })}</span>
        <span className="receipts-details-value strong">{formatNetValue(row.net_value)}</span>
      </div>
      <div className="receipts-details-row">
        <span className="receipts-details-label">{t({ en: 'Status', ar: 'الحالة' })}</span>
        <span className="receipts-details-value">
          {row.is_canceled
            ? t({ en: 'Voided', ar: 'ملغى' })
            : row.is_voucher_posted
            ? t({ en: 'Posted (read-only)', ar: 'مُرحّل (للقراءة فقط)' })
            : row.in_current_shift
            ? t({ en: 'Open (current shift)', ar: 'مفتوح (الوردية الحالية)' })
            : row.in_open_shift
            ? t({ en: 'Open (another cashier\'s shift)', ar: 'مفتوح (وردية كاشير آخر)' })
            : t({ en: 'Closed shift', ar: 'وردية مغلقة' })}
        </span>
      </div>

      {/* A voided receipt keeps its View and Print buttons — it is still a
          document — but the banner says plainly that it counts for nothing,
          which is the question anyone opening it is actually asking. */}
      {row.is_canceled && (
        <div className="receipts-voided-banner" role="status">
          <strong>{t({ en: 'This receipt was voided', ar: 'تم إلغاء هذا الإيصال' })}</strong>
          <span>
            {t({
              en: 'It does not count towards the shift, the Z-report or stock.',
              ar: 'لا يُحتسب ضمن الوردية ولا تقرير Z ولا المخزون.',
            })}
          </span>
        </div>
      )}

      <div className="receipts-details-actions">
        <button
          type="button"
          className="receipts-btn receipts-btn-secondary"
          onClick={() => onView?.(row.id)}
        >
          {t({ en: 'View', ar: 'عرض' })}
        </button>
        <button
          type="button"
          className="receipts-btn receipts-btn-secondary"
          onClick={() => onReprint?.(row.id)}
        >
          {t({ en: 'Print', ar: 'طباعة' })}
        </button>
        <button
          type="button"
          className="receipts-btn receipts-btn-secondary"
          onClick={() => onExchange?.(row.id)}
          disabled={row.is_return || row.is_canceled === true}
          title={
            row.is_canceled
              ? t({ en: 'This receipt was voided — there is nothing to return', ar: 'تم إلغاء هذا الإيصال — لا يوجد ما يُرتجع' })
              : row.is_return
              ? t({ en: 'This is already a return/exchange receipt', ar: 'هذا الإيصال هو بالفعل مرتجع / استبدال' })
              : t({ en: 'Return or exchange items from this invoice', ar: 'إرجاع أو استبدال أصناف من هذه الفاتورة' })
          }
        >
          {t({ en: 'Return / Exchange', ar: 'مرتجع / استبدال' })}
        </button>
        {/* Rendered only for a user who holds `Cancel Order` — a cashier
            without it should not see a control they can never use. Whether
            it is ENABLED is the server's `can_void`, which also knows about
            posted vouchers, closed shifts, gift cards and existing returns. */}
        {canVoid && (
          <button
            type="button"
            className="receipts-btn receipts-btn-danger"
            onClick={onVoid}
            disabled={row.can_void !== true}
            title={
              row.can_void === true
                ? t({ en: 'Cancel this receipt', ar: 'إلغاء هذا الإيصال' })
                : row.void_blocked_reason ??
                  t({ en: 'This receipt cannot be voided', ar: 'لا يمكن إلغاء هذا الإيصال' })
            }
          >
            {t({ en: 'Void', ar: 'إلغاء' })}
          </button>
        )}
        <button
          type="button"
          className="receipts-btn receipts-btn-primary"
          onClick={() => onEdit?.(row.id)}
          disabled={!row.can_edit}
          title={
            row.can_edit
              ? t({ en: 'Edit this receipt', ar: 'تعديل هذا الإيصال' })
              : t({ en: 'Editing is disabled (closed shift, posted voucher, no open shift of your own, or no permission)', ar: 'التعديل معطّل (وردية مغلقة أو سند مُرحّل أو لا توجد وردية مفتوحة لك أو لا توجد صلاحية)' })
          }
        >
          {t({ en: 'Edit', ar: 'تعديل' })}
        </button>
      </div>
    </div>
  );
}

interface PaginationProps {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}

function Pagination({ page, totalPages, onChange }: PaginationProps) {
  const { t } = useLang();
  // Mirror the legacy "First / Previous / 1 2 3 / Next / Last" pattern.
  // Show up to 5 numeric pages around the current page.
  const pageNumbers = useMemo(() => {
    const window = 2;
    const start = Math.max(1, page - window);
    const end = Math.min(totalPages, page + window);
    const out: number[] = [];
    for (let p = start; p <= end; p += 1) out.push(p);
    return out;
  }, [page, totalPages]);

  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <nav className="receipts-pagination" aria-label={t({ en: 'Pagination', ar: 'ترقيم الصفحات' })}>
      <button
        type="button"
        className="receipts-page-btn"
        disabled={!canPrev}
        onClick={() => onChange(1)}
      >
        {t({ en: 'First', ar: 'الأولى' })}
      </button>
      <button
        type="button"
        className="receipts-page-btn"
        disabled={!canPrev}
        onClick={() => onChange(page - 1)}
      >
        {t({ en: 'Previous', ar: 'السابقة' })}
      </button>
      {pageNumbers.map((p) => (
        <button
          key={p}
          type="button"
          aria-current={p === page ? 'page' : undefined}
          className={`receipts-page-btn ${p === page ? 'active' : ''}`}
          onClick={() => onChange(p)}
        >
          {p}
        </button>
      ))}
      <button
        type="button"
        className="receipts-page-btn"
        disabled={!canNext}
        onClick={() => onChange(page + 1)}
      >
        {t({ en: 'Next', ar: 'التالية' })}
      </button>
      <button
        type="button"
        className="receipts-page-btn"
        disabled={!canNext}
        onClick={() => onChange(totalPages)}
      >
        {t({ en: 'Last', ar: 'الأخيرة' })}
      </button>
    </nav>
  );
}

interface EmptyStateProps {
  scope: ReceiptListScope;
  search: string;
  hasOpenShift: boolean;
  dateFrom: string;
  dateTo: string;
  onClearDates: () => void;
}

function ReceiptsEmptyState({ scope, search, hasOpenShift, dateFrom, dateTo, onClearDates }: EmptyStateProps) {
  const { t } = useLang();
  // Checked BEFORE the shift and search messages: the range is on by default,
  // so it is the most likely reason a cashier is looking at an empty table,
  // and "no receipts in this shift" would send them hunting for the wrong
  // thing. Naming the window is what makes the Clear button findable.
  if (dateFrom || dateTo) {
    const window = dateFrom && dateTo
      ? (dateFrom === dateTo ? dateFrom : `${dateFrom} → ${dateTo}`)
      : dateFrom
        ? t({ en: `since ${dateFrom}`, ar: `منذ ${dateFrom}` })
        : t({ en: `until ${dateTo}`, ar: `حتى ${dateTo}` });
    return (
      <div className="receipts-state">
        <strong>{t({ en: `No receipts for ${window}`, ar: `لا توجد إيصالات في ${window}` })}</strong>
        <p>
          {t({
            en: 'Widen the dates, or clear them to search every date.',
            ar: 'وسّع نطاق التواريخ أو امسحها للبحث في كل التواريخ.',
          })}
        </p>
        {/* The search stays as typed, so a phone or name that matched
            nothing today is looked up across every date in one tap. */}
        <button type="button" className="receipts-btn receipts-btn-primary" onClick={onClearDates}>
          {t({ en: 'Show all dates', ar: 'عرض كل التواريخ' })}
        </button>
      </div>
    );
  }
  if (scope === 'current_shift' && !hasOpenShift) {
    return (
      <div className="receipts-state">
        <strong>{t({ en: 'No open shift', ar: 'لا توجد وردية مفتوحة' })}</strong>
        <p>{t({ en: 'Open a shift to see today\'s receipts. Switch to "All" to look up older receipts.', ar: 'افتح وردية لعرض إيصالات اليوم. بدّل إلى "الكل" للبحث عن الإيصالات الأقدم.' })}</p>
      </div>
    );
  }
  if (search.trim().length > 0) {
    return (
      <div className="receipts-state">
        <strong>{t({ en: `No receipts match "${search}"`, ar: `لا توجد إيصالات تطابق "${search}"` })}</strong>
        <p>{t({ en: 'Try a different order number, customer name, or phone.', ar: 'جرّب رقم طلب أو اسم عميل أو هاتفاً مختلفاً.' })}</p>
      </div>
    );
  }
  if (scope === 'current_shift') {
    return (
      <div className="receipts-state">
        <strong>{t({ en: 'No receipts in this shift', ar: 'لا توجد إيصالات في هذه الوردية' })}</strong>
        <p>{t({ en: 'Receipts appear here as you complete sales.', ar: 'تظهر الإيصالات هنا عند إتمام المبيعات.' })}</p>
      </div>
    );
  }
  return (
    <div className="receipts-state">
      <strong>{t({ en: 'No receipts yet', ar: 'لا توجد إيصالات بعد' })}</strong>
    </div>
  );
}
